/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { GridContainer, GridItem, GridRow } from "@tvg/fuse/grid";
import { useMainState } from "client/state/Main";
import { handleOpenModal } from "client/utils/searchUtils";
import React, { useRef, useState } from "react";
import { ComplexHeaderProps } from "types/common";
import HeaderLogo from "../../../common/components/HeaderLogo";
import styles from "./ComplexHeader.module.css";
import IconWrapper from "./iconwrapper/IconWrapper";
import MenuIconButton from "./menu-icon-button/MenuIconButton";
import NavigationBar from "./navigation-bar/NavigationBar";
import MobileSearchSidepanel from "./search/MobileSearchSidepanel";
import Search from "./search/Search";
import SlideOutMenu from "./slideout-menu/SlideOutMenu";

const ComplexHeader: React.FC<ComplexHeaderProps> = ({ brand }) => {
  const isCreditCustomer = useMainState((state) => state.isCreditCustomer);
  const { isMenuSlideOutOpen, setIsMenuSlideOutOpen } = useMainState();
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement | null>(null);

  const menuButtonClickHandler = (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => {
    e.preventDefault();

    // Only toggle menu on mouse click or Enter/Space key press
    if (
      e.type === "click" ||
      (e.type === "keydown" &&
        "key" in e &&
        (e.key === "Enter" || e.key === " "))
    ) {
      !isMenuSlideOutOpen && setIsMenuSlideOutOpen(true);
    }
  };

  const closeButtonClickHandler = () =>
    isMenuSlideOutOpen && setIsMenuSlideOutOpen(false);

  const handleMobileSearchClick = async (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    event.preventDefault();

    try {
      const modalOpened = await handleOpenModal(event);

      if (modalOpened) {
        // Autosuggest succeeded - remove focus from mobile search anchor
        const searchAnchor = document.querySelector(
          '[data-testid="mobile-header-search"]',
        );
        if (searchAnchor && document.activeElement === searchAnchor) {
          (searchAnchor as HTMLElement).blur();
        }
      } else {
        // Fall back to mobile search sidepanel
        setIsMobileSearchOpen(true);
      }
    } catch (error) {
      console.error("Error opening autosuggest modal:", error);
      // Fall back to mobile search sidepanel on error
      setIsMobileSearchOpen(true);
    }
  };

  const handleMobileSearchClose = () => {
    setIsMobileSearchOpen(false);
  };

  return (
    <header
      ref={menuRef}
      className={`${styles.complexHeader} navi-header-fe-complex-header`}
      data-testid="complex-header"
    >
      <GridContainer
        className={`${styles.complexHeader__topGridContainer} complex-header__top-grid-container`}
      >
        <GridRow
          className={`${styles.complexHeader__topGridRow} complex-header__top-grid-row`}
        >
          <div
            className={`${styles.complexHeader_leftWrapper} complex-header_left-wrapper`}
          >
            <GridItem
              className={`${styles.complexHeader__hamburgerMenuWrapper} complex-header__hamburger-menu-wrapper`}
            >
              <MenuIconButton
                dataTestId="navi-header-fe-menu-icon-button-test-id"
                onMenuButtonClick={menuButtonClickHandler}
              />
              <SlideOutMenu
                brand={brand}
                closeButtonClick={closeButtonClickHandler}
                dataTestId="navi-header-fe-slide-out-menu-test-id"
                isCreditCustomer={isCreditCustomer}
                isOpen={isMenuSlideOutOpen}
              />
            </GridItem>
            <GridContainer
              className={`${styles.complexHeader__brandLogoWrapper} complex-header__brand-logo-wrapper`}
              data-testid="complex-header-brand-logo-container"
            >
              <HeaderLogo brand={brand} />
            </GridContainer>
          </div>

          <GridItem
            className={`${styles.complexHeader__searchWrapper} complex-header__search-wrapper`}
          >
            <Search />
          </GridItem>

          <IconWrapper onSearchClick={handleMobileSearchClick} />
        </GridRow>
      </GridContainer>

      <GridContainer
        className={`${styles.complexHeader__navGridContainer} complex-header__nav-grid-container`}
      >
        <GridRow className="complex-header__nav-grid-row">
          <GridItem
            className={`${styles.complexHeader__navWrapper} complex-header__nav-wrapper`}
          >
            <NavigationBar brand={brand} />
          </GridItem>
        </GridRow>
      </GridContainer>

      <MobileSearchSidepanel
        isOpen={isMobileSearchOpen}
        onClose={handleMobileSearchClose}
      />
    </header>
  );
};

export default ComplexHeader;
